// @ts-check
// Cliente de servicio de autenticación por sesión + CSRF (TSK-0082,
// MOD-FRONTEND-CORE, sobre MOD-IDENTITY/TSK-0047 ya done). Mismo patrón que
// `exercise-client.js`: solo usa `apiRequest` de `api-client.js` -- nunca
// `fetch` directamente --, mapea snake_case (contrato real del backend) a
// camelCase, y deja que `ApiError`/`classifyStatus` (`api-errors.js`)
// clasifiquen los fallos sin capturarlos aquí (el llamador decide cómo
// mostrarlos, igual que en `obtenerEjercicioPorSlug`).
//
// Contrato exacto verificado en `backend/src/app/schemas/auth.py` y
// `backend/src/app/routers/v1/auth.py` (TSK-0047 done), NO asumido desde
// docs/03-frontend.md:
//   - GET  /auth/groups    -> 200 {items: [{codigo, nombre}]} (SIN sesión:
//     el formulario de registro lo necesita antes de que exista ninguna)
//   - GET  /auth/registro  -> 200 {abierto} (SIN sesión, 2026-09-13: la
//     página de entrada lo pide para decidir si enseña «Crear una cuenta»)
//   - POST /auth/register  body: RegistroRequest {username, password,
//     nombre_visible, fecha_nacimiento?} -> 201 UsuarioResponse
//     {id, email, nombre_visible, rol}. Sin campo `rol` en el request
//     (DEC-020, el backend lo ignora por diseño).
//   - POST /auth/login     body: LoginRequest {username, password} -> 200
//     LoginResponse {usuario_id, csrf_token}. Deliberadamente SIN perfil
//     completo ni token de sesión en claro (viaja solo en cookie HttpOnly).
//   - POST /auth/logout    sin body, requiere cookie de sesión + header
//     `X-CSRF-Token` (verify_csrf) -> 204 sin contenido.
//   - GET  /auth/me        sin body -> 200 MeResponse {usuario, csrf_token}
//     o 401 si no hay sesión válida.
//
// Discrepancia documentada (criterio de aceptación de TSK-0082): docs/03-
// frontend.md §11 no fija nombres de campo concretos (es agnóstico de
// contrato); no se detecta contradicción entre ese documento y
// `schemas/auth.py` -- se sigue el código real del backend en todos los
// nombres de campo, tal como exige la tarea.
//
// Todas las funciones pasan `credentials: 'include'` explícito a
// `apiRequest` (criterio de aceptación): `apiRequest` por defecto usa
// `same-origin`, insuficiente si el proxy de dev sirve la API en un origen
// distinto al del frontend -- sin `include` la cookie de sesión HttpOnly no
// viajaría y `/me` fallaría siempre con 401 aunque el login fuera correcto.

import { apiRequest } from './api-client.js';

/**
 * Datos del registro (DEC-102). NO incluye correo: los alumnos se registran
 * sin ningún dato personal.
 * @typedef {{
 *   username: string,
 *   password: string,
 *   grupoCodigo?: string|null,
 *   nombreVisible?: string|null,
 *   fechaNacimiento?: string|null,
 * }} DatosRegistro
 */

/**
 * @typedef {{ username: string, password: string }} Credenciales
 */

/**
 * Un grupo del catálogo público (`GET /auth/groups`).
 * @typedef {{ codigo: string, nombre: string }} GrupoPublico
 */

/**
 * @typedef {{
 *   id: string,
 *   username: string,
 *   email: string|null,
 *   nombreVisible: string,
 *   rol: string,
 *   preferenciaTema: string,
 * }} UsuarioPublico
 */

/**
 * @typedef {{ usuarioId: string, csrfToken: string }} SesionIniciada
 */

/**
 * @typedef {{ usuario: UsuarioPublico, csrfToken: string }} EstadoSesion
 */

/**
 * La preferencia de tema cuando el servidor no la manda: la misma que él pone
 * a una cuenta nueva. `profile-client.js` usa esta, no otra (DEC-180).
 */
export const PREFERENCIA_TEMA_POR_DEFECTO = 'automatico';

/**
 * Mapea la respuesta cruda de `UsuarioResponse` (snake_case) al tipo interno
 * de frontend (camelCase). Deliberadamente defensivo con `unknown`: no
 * confía en que la red entregue exactamente la forma esperada (mismo
 * criterio que `mapearEjercicioPublico` en `exercise-client.js`).
 *
 * Incluye `preferencia_tema` -> `preferenciaTema` (TSK-0108, DEC-044): el
 * backend (TSK-0107) añadió este campo a `UsuarioResponse`, expuesto tanto
 * en `/auth/register` como en `/auth/me`. Por defecto `'automatico'` si el
 * backend aún no lo envía (compatibilidad hacia atrás durante el
 * despliegue), nunca `undefined`.
 * @param {unknown} datos
 * @returns {UsuarioPublico}
 */
function mapearUsuarioResponse(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    id: String(crudo.id ?? ''),
    username: String(crudo.username ?? ''),
    // `null` y no cadena vacía: una cuenta de alumno NO tiene correo
    // (DEC-102), y eso es distinto de "tiene uno vacío". La interfaz decide
    // si enseña el dato o lo omite.
    email: typeof crudo.email === 'string' && crudo.email !== '' ? crudo.email : null,
    nombreVisible: String(crudo.nombre_visible ?? ''),
    rol: String(crudo.rol ?? ''),
    preferenciaTema: String(crudo.preferencia_tema ?? PREFERENCIA_TEMA_POR_DEFECTO),
  };
}

/**
 * Mapea la respuesta cruda de `LoginResponse` (snake_case) al tipo interno
 * de frontend (camelCase).
 * @param {unknown} datos
 * @returns {SesionIniciada}
 */
function mapearLoginResponse(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    usuarioId: String(crudo.usuario_id ?? ''),
    csrfToken: String(crudo.csrf_token ?? ''),
  };
}

/**
 * Mapea la respuesta cruda de `MeResponse` (snake_case) al tipo interno de
 * frontend (camelCase).
 * @param {unknown} datos
 * @returns {EstadoSesion}
 */
function mapearMeResponse(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    usuario: mapearUsuarioResponse(crudo.usuario),
    csrfToken: String(crudo.csrf_token ?? ''),
  };
}

/**
 * Catálogo de grupos de clase para el desplegable de registro (DEC-102).
 *
 * Se pide SIN sesión, que es justo para lo que existe el endpoint. Descarta
 * las entradas sin código en vez de dejarlas pasar: una opción sin valor en
 * un `<select>` es una opción que no se puede elegir pero sí seleccionar.
 * @param {{ signal?: AbortSignal }} [opciones]
 * @returns {Promise<GrupoPublico[]>}
 */
export async function obtenerGrupos(opciones = {}) {
  const respuesta = await apiRequest('/auth/groups', { signal: opciones.signal });
  const crudo = /** @type {Record<string, unknown>} */ (respuesta ?? {});
  const items = Array.isArray(crudo.items) ? crudo.items : [];
  return items
    .map((item) => {
      const g = /** @type {Record<string, unknown>} */ (item ?? {});
      return { codigo: String(g.codigo ?? ''), nombre: String(g.nombre ?? '') };
    })
    .filter((g) => g.codigo !== '');
}

/**
 * Si el registro público de alumnos está abierto (`GET /auth/registro`,
 * 2026-09-13). El administrador lo abre y lo cierra desde el panel; cerrado,
 * `POST /auth/register` responde 403 `registro_cerrado`.
 *
 * Se pide SIN sesión, como el catálogo de grupos. Solo un `true` de verdad
 * cuenta como abierto: cualquier otra cosa se lee como cerrado, que es la
 * lectura que no manda a nadie a un formulario que no le va a dejar. Propaga
 * `ApiError` sin capturarla: la página decide qué hacer si no se puede saber.
 * @param {{ signal?: AbortSignal }} [opciones]
 * @returns {Promise<{ abierto: boolean }>}
 */
export async function obtenerEstadoRegistro(opciones = {}) {
  const respuesta = await apiRequest('/auth/registro', { signal: opciones.signal });
  const crudo = /** @type {Record<string, unknown>} */ (respuesta ?? {});
  return { abierto: crudo.abierto === true };
}

/**
 * Registra una cuenta nueva (`POST /api/v1/auth/register`). Siempre crea un
 * usuario con rol estudiante (DEC-020): este cliente no envía ni acepta un
 * campo `rol`, reflejando que `RegistroRequest` no lo declara.
 * Propaga `ApiError` sin capturarla (p.ej. 'conflict' si el usuario ya está
 * en uso, 409): la página que llama decide cómo mostrarlo.
 * @param {DatosRegistro} datos
 * @returns {Promise<UsuarioPublico>}
 */
export async function registrar(datos) {
  const cuerpo = /** @type {Record<string, unknown>} */ ({
    username: datos.username,
    password: datos.password,
  });
  if (datos.grupoCodigo) {
    cuerpo.grupo_codigo = datos.grupoCodigo;
  }
  // Opcional desde DEC-102: si no se envía, el servidor usa el propio
  // usuario como nombre visible.
  if (datos.nombreVisible) {
    cuerpo.nombre_visible = datos.nombreVisible;
  }
  if (datos.fechaNacimiento !== undefined && datos.fechaNacimiento !== null) {
    cuerpo.fecha_nacimiento = datos.fechaNacimiento;
  }

  const respuesta = await apiRequest('/auth/register', {
    method: 'POST',
    body: cuerpo,
    credentials: 'include',
  });
  return mapearUsuarioResponse(respuesta);
}

/**
 * Inicia sesión (`POST /api/v1/auth/login`). El backend fija la cookie de
 * sesión `HttpOnly`+`Secure`(según entorno)+`SameSite` server-side; esta
 * función nunca ve ni maneja el token de sesión en claro (no viaja en el
 * cuerpo de la respuesta, por diseño de `LoginResponse`). Solo devuelve
 * `usuarioId` + `csrfToken`: el perfil completo se obtiene después con
 * `obtenerUsuarioActual()` (patrón exigido por docs/03-frontend.md §11.1).
 * Propaga `ApiError` sin capturarla (p.ej. 'unauthorized', 401, en
 * credenciales incorrectas): no guarda nada en el store si falla, porque
 * ese es responsabilidad de quien orquesta login+store, no de este cliente.
 * @param {Credenciales} credenciales
 * @returns {Promise<SesionIniciada>}
 */
export async function iniciarSesion(credenciales) {
  const respuesta = await apiRequest('/auth/login', {
    method: 'POST',
    body: {
      username: credenciales.username,
      password: credenciales.password,
    },
    credentials: 'include',
  });
  return mapearLoginResponse(respuesta);
}

/**
 * Cierra sesión (`POST /api/v1/auth/logout`). El backend exige sesión válida
 * (401 si no) y CSRF válido (403 si no, `verify_csrf`): el header
 * `X-CSRF-Token` debe adjuntarse explícitamente porque `logout` es una
 * mutación (ver `session-store.js#cabeceraCsrf`). Responde 204 sin
 * contenido; `apiRequest` devuelve `null` en ese caso (sin `content-type`
 * JSON), consistente con no tener nada útil que mapear aquí.
 * @param {{ csrfToken: string }} opciones - Token CSRF vigente (ver
 *   `session-store.js#obtenerCsrfToken`); esta función no lee el store
 *   directamente (services no gestionan estado, solo hablan con la red).
 * @returns {Promise<null>}
 */
export async function cerrarSesion(opciones) {
  const respuesta = await apiRequest('/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': opciones.csrfToken },
  });
  return /** @type {null} */ (respuesta);
}

/**
 * Obtiene el estado del usuario autenticado (`GET /api/v1/auth/me`) junto a
 * un token CSRF fresco. Propaga `ApiError` sin capturarla, incluido el caso
 * 401 ('unauthorized', "no autenticado"): distinguir ese caso de un error
 * real de red/servidor es responsabilidad de quien la llama
 * (`session-store.js#cargarSesionActual`), no de este cliente -- mismo
 * criterio que `obtenerEjercicioPorSlug`, que tampoco decide cómo se
 * interpreta un fallo.
 * @returns {Promise<EstadoSesion>}
 */
export async function obtenerUsuarioActual() {
  const respuesta = await apiRequest('/auth/me', {
    credentials: 'include',
  });
  return mapearMeResponse(respuesta);
}
